import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  Fragment,
} from "react";
import { Outlet } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, dispatch, useDispatch } from "react-redux";
import { setCurrentUser } from "../redux/slices/currentuserslice";
import {
  setSideBarDetailVisible,
  setSideBarVisible,
} from "../redux/slices/appslice";
import { Button } from "@progress/kendo-react-buttons";
import {
  Drawer,
  DrawerContent,
  DrawerItem,
} from "@progress/kendo-react-layout";
import { APP_HEADER_HEIGHT } from "../settings";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Dialog } from "@progress/kendo-react-dialogs";
import { ChangePasswordForm } from "./changepasswordform";
import {
  canCreateReports,
  canEditReports,
} from "../routing/authorizationhandlers";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const AppBody = (props) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.currentUser);
  const currentTheme = useSelector((state) => state.currentTheme);
  const appearance = useSelector((state) => state.appearance);
  const resolutionHeight = window.screen.height;
  const appBarHeight = APP_HEADER_HEIGHT; // resolutionHeight * 0.05;
  const { t } = useTranslation();
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => window.matchMedia("(max-width: 1024px)").matches,
  );

  const bodyHeight = "calc(100vh - " + appBarHeight + "px)";

  const navigate = useNavigate();

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1024px)");
    const updateViewport = (event) => setIsNarrowViewport(event.matches);

    mediaQuery.addEventListener("change", updateViewport);
    return () => mediaQuery.removeEventListener("change", updateViewport);
  }, []);

  const sideBarVisible = useSelector((state) => state.app.sideBarVisible);
  const sideBarDetailVisible = useSelector(
    (state) => state.app.sideBarDetailVisible,
  );
  const sideBarType =
    currentUser && currentUser.IsAuthenticated ? currentUser.SideBarType : 0;
  let reportsMenu =
    currentUser && currentUser.IsAuthenticated ? currentUser.ReportsMenu : [];

  let sideBarExpanded =
    (sideBarType == 1 && sideBarVisible) ||
    (sideBarType == 2 && sideBarVisible && sideBarDetailVisible);
  let sideBarMini =
    sideBarType == 1 ? false : sideBarVisible && !sideBarDetailVisible;
  let sideBarMode =
    isNarrowViewport || sideBarType == 1
      ? "overlay"
      : !sideBarVisible || !sideBarDetailVisible
        ? "push"
        : "overlay";
  let sideBarIsNotCollapsed =
    (sideBarType == 1 && sideBarVisible) ||
    (sideBarType == 2 && sideBarDetailVisible);

  const _sideBarType = useRef(null);
  const _sideBarIsNotCollapsed = useRef(0);

  _sideBarType.current = sideBarType;
  _sideBarIsNotCollapsed.current = sideBarIsNotCollapsed;

  const closeOnClickOutsieSideBar = (e) => {
    if (
      _sideBarIsNotCollapsed.current &&
      !e.target.closest(".k-drawer-item") &&
      !e.target.closest(".site-burger")
    ) {
      if (_sideBarType.current == 1) {
        dispatch(setSideBarDetailVisible(false));
        dispatch(setSideBarVisible(false));
      } else {
        dispatch(setSideBarDetailVisible(false));
        dispatch(setSideBarVisible(true));
      }
    }
  };

  useEffect(() => {
    window.addEventListener("click", closeOnClickOutsieSideBar);

    return () => {
      window.removeEventListener("click,", closeOnClickOutsieSideBar);
    };
  }, []);

  reportsMenu = reportsMenu?.map((item) => {
    const { parentId, ...others } = item;

    if (parentId !== undefined) {
      //is child => get the parent item
      const parent = reportsMenu?.find((e) => e.id === parentId);

      return {
        ...others,
        parentId: parentId,
        visible:
          ((sideBarType == 2 && sideBarDetailVisible) ||
            (sideBarType == 1 && sideBarVisible)) &&
          parent["data-expanded"],
      };
    }

    return item;
  });

  if (
    currentUser &&
    currentUser.IsAuthenticated &&
    canCreateReports(currentUser) &&
    reportsMenu
  )
    reportsMenu = [{ createReport: true }, ...reportsMenu];

  const CustomItem = (props) => {
    const { visible, createReport, referenceKey, parentId, ...others } = props;

    if (props.createReport)
      return (
        <DrawerItem {...others}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              overflow: "hidden",
              padding: "0 8px",
            }}
            onClick={(e) => {
              dispatch(setSideBarDetailVisible(false));

              if (sideBarType == 1) dispatch(setSideBarVisible(false));

              navigate("/reportingdesign/new");
            }}
          >
            <span
              className={"k-icon k-i-file-add"}
              title="Create a new report"
            />
            <span className={"k-item-text"} style={{ flex: 1, marginLeft: 2 }}>
              {t("appbody.report")}
            </span>
          </div>
        </DrawerItem>
      );

    const arrowDir = props["data-expanded"]
      ? "k-i-arrow-chevron-up"
      : "k-i-arrow-chevron-down";

    //do not render if not visible
    if (props.visible === false) return null;

    //if group with no children do not render
    let isGroup = props["data-expanded"] !== undefined;

    if (isGroup && !reportsMenu?.find((e) => e.parentId === props.id))
      return null;

    return (
      <React.Fragment>
        {
          <DrawerItem {...others}>
            {isGroup && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  overflow: "hidden",
                  padding: "0 8px",
                }}
              >
                <span className={"k-icon " + props.icon} title={props.text} />
                <span
                  className={"k-item-text"}
                  style={{ flex: 1, fontWeight: 500 }}
                >
                  {props.text}
                </span>
                <span
                  className={"k-icon " + arrowDir}
                  style={{ position: "absolute", right: 10 }}
                />
              </div>
            )}
            {!isGroup && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  overflow: "hidden",
                  padding: "0 8px",
                }}
              >
                <span
                  className={"k-item-text"}
                  style={{ flex: 1, marginLeft: 36 }}
                >
                  {props.text}
                </span>
                <Button
                  className="k-item-view"
                  style={{}}
                  icon="eye"
                  fillMode="flat"
                  onClick={(e) => {
                    dispatch(setSideBarDetailVisible(false));

                    if (sideBarType == 1) dispatch(setSideBarVisible(false));

                    navigate("/reportingview/" + props.referenceKey);
                  }}
                />
                {canEditReports(currentUser) && (
                  <Button
                    className="k-item-edit"
                    style={{}}
                    icon="edit"
                    fillMode="flat"
                    onClick={(e) => {
                      dispatch(setSideBarDetailVisible(false));

                      if (sideBarType == 1) dispatch(setSideBarVisible(false));

                      navigate("/reportingdesign/" + props.referenceKey);
                    }}
                  />
                )}
              </div>
            )}
          </DrawerItem>
        }
      </React.Fragment>
    );
  };

  const handleOnSideBarItemClick = (e) => {
    const currentItem = e.itemTarget.props;
    const isParent = currentItem["data-expanded"] !== undefined;
    const nextExpanded = !currentItem["data-expanded"];

    //console.log("SIDEBAR ITEM CLICK", currentItem)
    // if (currentItem.createReport)
    // {
    //   dispatch(setSideBarDetailVisible(false));

    //   if (sideBarType == 1)
    //     dispatch(setSideBarVisible(false));

    //   //navigate("/reportingdesign/new");
    //   window.location.href = "/reportingdesign/new";
    //   return;
    // }

    if (isParent) {
      const newData = currentUser.ReportsMenu?.map((item) => {
        const { selected, id, ...others } = item;
        const isCurrentItem = currentItem.id === id;
        const itemIsParent = item["data-expanded"] !== undefined;
        let currentExpanded = item["data-expanded"] === true ? true : false;

        let obj = {
          selected: isCurrentItem,
          id,
          ...others,
        };

        if (itemIsParent) {
          if (!isCurrentItem) obj["data-expanded"] = currentExpanded;
          else {
            //current item
            if (_sideBarIsNotCollapsed) obj["data-expanded"] = nextExpanded;
            else obj["data-expanded"] = true;
          }
        }

        return obj;
      });

      dispatch(setCurrentUser({ ...currentUser, ReportsMenu: newData }));

      if (sideBarType == 2 && sideBarDetailVisible == false)
        dispatch(setSideBarDetailVisible(true));
    }
  };

  return (
    <div className="app-body">
      {currentUser &&
        currentUser.IsAuthenticated &&
        currentUser.ReportsMenu &&
        currentUser.ReportsMenu.length > 0 && (
          <Drawer
            expanded={sideBarExpanded}
            mini={sideBarMini}
            mode={sideBarMode}
            position={appearance.isRightToLeft ? "end" : "start"}
            items={reportsMenu}
            onSelect={handleOnSideBarItemClick}
            item={CustomItem}
          >
            <DrawerContent>
              <Outlet />
            </DrawerContent>
          </Drawer>
        )}
      {(!currentUser ||
        !currentUser.IsAuthenticated ||
        !currentUser.ReportsMenu ||
        currentUser.ReportsMenu.length < 1) && (
        <Drawer>
          <DrawerContent>
            <Outlet />
          </DrawerContent>
        </Drawer>
      )}

      {currentUser &&
        currentUser.IsAuthenticated &&
        !currentUser.Subscription?.FirstLoginCompleted &&
        !currentUser.IsFederationRootUser && (
          <Dialog className="change-password-form-dialog" title={null}>
            <ChangePasswordForm
              currentTheme={currentTheme}
              currentUser={currentUser}
              onCancel={() => {}}
            />
          </Dialog>
        )}
      <style>
        {`
                .app-body{
                    display: flex;
                    height: ${bodyHeight};
                    overflow-y: auto;
                    /*background-color: ${currentTheme.bodyBg};*/
                 }

                 .k-splitter
                 {
                    background-color: transparent; /*${currentTheme.componentBg};*/
                 }

                 .k-card
                 {
                    background-color: ${currentTheme.bodyBg};
                 }

                 .k-input-solid, .k-dropdown-list
                 {
                    background-color: ${currentTheme.bodyBg};
                    color: ${currentTheme.bodyText};
                 }

                 .k-drawer-container
                 {
                    width: 100%;
                    min-width: 0;
                    height: 100%;
                 }
                 

                 .k-drawer-overlay
                 {
                    height: 100%;
                    width: 100%;
                 }

                 .k-drawer-content
                 {
                    height: 100%;
                    flex: 1 1 auto;
                    min-width: 0;
                    width: auto;
                    max-width: 100%;
                    overflow: auto;
                 }

                 .k-drawer-overlay .k-overlay,
                 .k-drawer-overlay .k-drawer 
                 {
                    z-index: 10030;
                    top:42px;
                 }

                 .k-drawer-mini .k-drawer.k-drawer-start
                 {
                  
                  border-right: 1.5px solid ${currentTheme.componentBorderColor};
                  background-color: ${currentTheme.sideBarMiniBgColor};
                 }

                 .k-drawer-expanded.k-drawer-container .k-drawer
                 {
                  min-width: 475px;
                  background-color: ${currentTheme.sideBarMiniBgColor};
                  border-right: 1.5px solid ${currentTheme.componentBorderColor};
                 } 

                 .k-drawer-expanded.k-drawer-container .k-drawer .k-drawer-wrapper
                 {
                  width: 475px!important;
                 }

                 .k-drawer-expanded.k-drawer-container .k-overlay
                 {
                  background-color: transparent;
                 }

                 .k-drawer-mini .k-i-arrow-chevron-down, .k-drawer-mini .k-i-arrow-chevron-up
                 {
                  display: none !important;
                 } 

                 .k-drawer-item
                 {
                  padding: 8px 0;
                  border-bottom: 1px solid ${currentTheme.componentBorderColor};
                 }

                 .k-drawer-item:not([data-expanded])
                 {
                  border-bottom: 1px dashed ${currentTheme.componentBorderColor};
                 }

                 .k-drawer-separator
                 {
                  padding: 0;
                  height: 0;
                  border-bottom: none !important;
                 }

                 .k-drawer-item:focus
                 {
                  box-shadow: none;
                  background-color: ${currentTheme.componentBorderColor};
                 }
                 
                 .k-drawer-item .k-button
                 {
                  height: 24px;
                 }

                  @media (max-width: 1024px)
                 {
                   .k-drawer-expanded.k-drawer-container .k-drawer
                   {
                     min-width: 0;
                     width: min(475px, 90vw);
                     max-width: 90vw;
                   }

                   .k-drawer-expanded.k-drawer-container .k-drawer .k-drawer-wrapper
                   {
                     width: min(475px, 90vw) !important;
                   }

                  .k-drawer-expanded.k-drawer-container .k-drawer .k-drawer-wrapper .k-drawer-items .k-drawer-item .k-item-edit
                   {
                     display: none !important;
                   }

                  .o-main-components-container, .o-mail-ChatHub-bubbles.o-mobile, .o-livechat-LivechatButton
                   {
                     display: none !important;
                   }
                 }

            `}
      </style>
    </div>
  );
};
